// web/src/components/ds/DsMenu.tsx — DS.5 (sprint aos-ds-shell, spec §2 D4).
//
// `@ds/overlays/Menu` (`trigger` + `items[]`) for the shell's dropdown menus:
// the sidebar avatar menu, the chat-header kebab and — through
// ContextMenuHost — the two context menus. What the adapter adds, and why:
//
//   * FOCUS ON OPEN. The DS piece handles ArrowDown/ArrowUp/Enter/Escape in
//     an `onKeyDown` on its wrapper, which only fires while focus is INSIDE
//     the wrapper — and it focuses nothing on open, so a menu opened by
//     right-click or by a click on the trigger ignored Escape and the arrow
//     keys until the pointer hovered a row. Radix focused the content on
//     open; this adapter does the same (the menu node gets `tabIndex=-1`
//     and focus in its mount callback) and hands focus back to the element
//     that had it when the menu closes, when nothing else took it.
//   * TEST IDS. `MenuItem` has no attribute slot and the divider renders no
//     label, so the menu node (`role="menu"`) and its `role="separator"`
//     rows are stamped with `data-testid`s in the same mount callback. Item
//     ids go on the label node the call site passes (`label` is a ReactNode).
//   * CONTROLLED OR NOT: `open`/`onOpenChange` as the piece has them, but the
//     adapter always knows the open state, which the mount callback needs.
//
// KEYBOARD DELTA vs Radix (spec D7, §6 row 4), measured at v0.2.0
// (Menu.tsx:224-248): ArrowDown, ArrowUp, Enter, Escape, with wrapping and
// dividers/headings/disabled skipped. The piece has no blur handler (closes
// on `mousedown` outside only) — TAB-TO-CLOSE is restored here (reviewer
// round 1): a `focusout` whose `relatedTarget` leaves the menu node closes
// it. NOT implemented, accepted and recorded: typeahead, Home/End.

import { useCallback, useEffect, useRef, useState, type ComponentProps } from 'react';
import Menu from '@ds/overlays/Menu';

type MenuProps = ComponentProps<typeof Menu>;

export type DsMenuItem = MenuProps['items'][number];

export interface DsMenuProps extends Omit<MenuProps, 'open' | 'onOpenChange' | 'ref'> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Stamped on the `role="menu"` node while open (never on the wrapper). */
  'data-testid'?: string;
  /** Stamped as `data-testid` on every `role="separator"` row while open. */
  dividerTestId?: string;
  /** Move focus into the menu on open (keyboard + Escape parity). Default true. */
  autoFocus?: boolean;
}

export function DsMenu({
  open,
  onOpenChange,
  'data-testid': menuTestId,
  dividerTestId,
  autoFocus = true,
  ...rest
}: DsMenuProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );
  // Read through a ref inside the mount callback (reviewer round 2): `setOpen`
  // changes identity with every inline `onOpenChange` a parent passes
  // (ContextMenuHost's callers pass one), and a callback ref whose identity
  // changes is re-invoked by React on the LIVE node — which re-focused the
  // menu, stacked a second focusout listener and overwrote `restoreFocusTo`
  // on every parent re-render (the Sidebar re-renders on socket updates and
  // its 30 s tick). The ref keeps the callback's identity stable.
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;

  // Stable identity: React re-invokes a callback ref only when its identity
  // changes, so a re-render while open must NOT re-run this (it would steal
  // focus back from the row the keyboard moved to).
  const menuRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      if (menuTestId) node.setAttribute('data-testid', menuTestId);
      if (dividerTestId) {
        node.querySelectorAll('[role="separator"]').forEach((el) => {
          el.setAttribute('data-testid', dividerTestId);
        });
      }
      // Tab-to-close: focus leaving the menu node (to anything outside it, or
      // to nowhere) closes; moving between rows stays inside and does not.
      node.addEventListener('focusout', (e: FocusEvent) => {
        const next = e.relatedTarget;
        if (next instanceof Node && node.contains(next)) return;
        if (isOpenRef.current) setOpenRef.current(false);
      });
      if (autoFocus) {
        const active = document.activeElement;
        restoreFocusTo.current = active instanceof HTMLElement ? active : null;
        node.tabIndex = -1;
        node.focus();
      }
    },
    [menuTestId, dividerTestId, autoFocus],
  );

  useEffect(() => {
    if (isOpen) return;
    const el = restoreFocusTo.current;
    restoreFocusTo.current = null;
    if (el && el.isConnected && document.activeElement === document.body) el.focus();
  }, [isOpen]);

  return <Menu ref={menuRef} open={isOpen} onOpenChange={setOpen} {...rest} />;
}

export default DsMenu;
